import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type { PermissionInfo, RoleWithPermissions } from '@news-cms/shared';
import { ApiError, api } from './api';
import { toast } from '@/components/ui/toaster';

interface RolesMatrixResponse {
  roles: RoleWithPermissions[];
  permissions: PermissionInfo[];
}

export const rolesMatrixQueryOptions = () =>
  queryOptions({
    queryKey: ['roles', 'matrix'] as const,
    queryFn: () => api<RolesMatrixResponse>('/api/admin/roles'),
    staleTime: 5 * 60_000,
  });

export function useUpdateRolePermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      roleId,
      permissionCodes,
    }: {
      roleId: string;
      permissionCodes: string[];
    }) => {
      const data = await api<{ roleId: string; permissionCodes: string[] }>(
        `/api/admin/roles/${roleId}/permissions`,
        {
          method: 'PUT',
          body: JSON.stringify({ permissionCodes }),
        },
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['roles', 'matrix'] });
      toast.success('บันทึกสิทธิ์เรียบร้อย');
    },
    onError: (e) => {
      if (e instanceof ApiError) {
        if (e.code === 'CANNOT_EDIT_SUPER_ADMIN') {
          toast.error('แก้ super_admin ไม่ได้ — เป็น role พื้นฐานที่ระบบล็อกไว้');
          return;
        }
        if (e.code === 'UNKNOWN_PERMISSION') {
          toast.error('Permission code ไม่ถูกต้อง');
          return;
        }
        if (e.code === 'FORBIDDEN') {
          toast.error('คุณไม่มีสิทธิ์แก้ role');
          return;
        }
      }
      toast.error('บันทึกไม่สำเร็จ');
    },
  });
}
